/**
 * S-27 › Blocklist manager (V8). Provenance-gated render — ARCH §3.1
 * correction 12 (normative):
 *   via profile|shield_log → full identity (legitimately known)
 *   via sr_id              → FULLY ANONYMIZED forever: ©blocklist.anon_sr + date
 *   via roulette_session   → display_name only
 * Unblock is keyed on the blocks-row id — anonymized entries expose no userId.
 */
import { useEffect, useState } from "react";
import { copy } from "@humble/shared";
import { api, isApiError } from "../../lib/api.js";
import { Button, Card, Skeleton, toast } from "../../design/index.js";
import "./safety.css";

interface BlockEntry {
  block_id: string;
  via: "profile" | "sr_id" | "roulette_session" | "shield_log";
  created_at: string;
  user?: { id: string; display_name: string | null; purged?: boolean };
  display_name?: string;
}

function entryLabel(e: BlockEntry): string {
  if (e.via === "sr_id") return copy.blocklist.anon_sr;
  if (e.via === "roulette_session") return `${e.display_name ?? "Stranger"} (Roulette)`;
  if (e.user?.purged) return "Deleted account";
  return e.user?.display_name ?? "Unknown";
}

export function BlocklistScreen() {
  const [entries, setEntries] = useState<BlockEntry[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = () => {
    api
      .get("/blocks")
      .then((rows) => setEntries((rows as unknown as BlockEntry[]) ?? []))
      .catch(() => setEntries([]));
  };
  useEffect(load, []);

  const unblock = async (blockId: string) => {
    setBusyId(blockId);
    try {
      await api.delete("/blocks/{blockId}", { params: { blockId } });
      setEntries((prev) => prev?.filter((e) => e.block_id !== blockId) ?? null);
      toast("Unblocked. Nothing old is restored — only future visibility.", "info");
    } catch (err) {
      toast(isApiError(err) ? err.message : "Unblock failed.", "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="safety-page" style={{ maxWidth: 560 }}>
      <h1>Blocklist</h1>
      <p className="text-dim">
        Blocking is mutual invisibility, everywhere, instantly. Unblocking only re-enables future
        deck appearance — conversations and matches closed by a block stay closed.
      </p>
      {entries === null ? (
        <Skeleton style={{ height: 120 }} radius={20} />
      ) : entries.length === 0 ? (
        <Card padding="lg">
          <p className="text-dim" style={{ margin: 0 }}>
            Nobody here. Your patience is remarkable.
          </p>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {entries.map((e) => (
            <Card key={e.block_id} padding="md">
              <div className="safety-row">
                <div className="safety-row__body">
                  <div className="safety-row__name">{entryLabel(e)}</div>
                  <div className="safety-row__meta">
                    Blocked {new Date(e.created_at).toLocaleDateString()}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={busyId === e.block_id}
                  onClick={() => void unblock(e.block_id)}
                >
                  Unblock
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
